import type { UserCourse, UserInfo } from '../../services/userManager';
import type {
  LegacyMigratePlanCourse,
  LegacyMigratePlanProduct,
  LegacyMigratePreview,
  LegacyMigrationRemaps,
} from '../../services/legacyUserManager';

export type AdminUserPreviewProduct = {
  product_id: number;
  id?: number;
  name: string;
  until_date?: string | null;
  category: string;
  experience_category?: string;
  source?: 'course' | 'video' | 'exposure' | 'performance';
  [key: string]: unknown;
};

export type AdminUserPreviewView = {
  userInfo: UserInfo;
  userCourses: UserCourse[];
  userProducts: AdminUserPreviewProduct[];
  remaps: LegacyMigrationRemaps;
  report: LegacyMigratePreview['plan']['report'];
  counts: {
    courses: number;
    courseProducts: number;
    videoProducts: number;
    clinicalProducts: number;
  };
};

const toIsoOrNull = (value: string | null | undefined): string | null => {
  if (value == null || value === '') return null;
  return String(value);
};

const courseNameByBeId = (
  remaps: LegacyMigrationRemaps
): Map<number, string> => {
  const map = new Map<number, string>();
  for (const row of remaps.courses) {
    map.set(Number(row.be_id), row.be_name || row.vapor_name || `Course ${row.be_id}`);
  }
  return map;
};

const productNameById = (
  remaps: LegacyMigrationRemaps
): Map<number, string> => {
  const map = new Map<number, string>();
  for (const row of remaps.courses) {
    if (row.product_id != null) {
      map.set(
        Number(row.product_id),
        row.be_name || row.vapor_name || `Product ${row.product_id}`
      );
    }
  }
  for (const row of remaps.videos) {
    map.set(
      Number(row.product_id),
      row.vapor_name || `Video product ${row.product_id}`
    );
  }
  return map;
};

const mapCourse = (
  draft: LegacyMigratePlanCourse,
  names: Map<number, string>
): UserCourse => {
  const courseId = Number(draft.course_id);
  return {
    courseId,
    courseName: names.get(courseId) ?? `Course ${courseId}`,
    current_section: Number(draft.current_section ?? 0),
    current_time: Number(draft.current_time ?? 0),
    completed_at: toIsoOrNull(draft.completed_at),
    passed_at: toIsoOrNull(draft.passed_at),
    has_review: Boolean(draft.review),
    review: draft.review as UserCourse['review'],
    answer_1: draft.answer_1,
    answer_2: draft.answer_2,
    answer_3: draft.answer_3,
    answer_4: draft.answer_4,
    answer_5: draft.answer_5,
  };
};

const LIFE_SUITE_PRODUCT_NAMES: Record<number, string> = {
  75: 'Augmented Exposure',
  76: 'Augmented Performance',
};

const mapProduct = (
  draft: LegacyMigratePlanProduct,
  names: Map<number, string>
): AdminUserPreviewProduct => {
  const productId = Number(draft.product_id);
  const isVideo = draft.source === 'video';
  const isClinical =
    draft.source === 'exposure' || draft.source === 'performance';
  const name =
    names.get(productId) ??
    LIFE_SUITE_PRODUCT_NAMES[productId] ??
    `Product ${productId}`;
  return {
    product_id: productId,
    id: productId,
    name,
    until_date: toIsoOrNull(draft.until_date),
    source: draft.source,
    category: isVideo ? 'video' : isClinical ? 'life_suite' : 'course',
    experience_category: isVideo
      ? `vapor video #${draft.vapor_ref_id}`
      : isClinical
        ? `vapor ${draft.source} cfg #${draft.vapor_ref_id}`
        : `vapor course #${draft.vapor_ref_id}`,
  };
};

/**
 * Convert migrate dry-run plan into the shapes expected by UserDashboard
 * / CoursesTab / ProductsTab (same visual surface as /admin/users/:id*).
 */
export const mapMigrationPlanToAdminUserView = (
  plan: LegacyMigratePreview['plan']
): AdminUserPreviewView => {
  const profile = plan.profile ?? {};
  const courseNames = courseNameByBeId(plan.remaps);
  const productNames = productNameById(plan.remaps);
  const userCourses = (plan.courses ?? []).map((c) =>
    mapCourse(c, courseNames)
  );
  const userProducts = (plan.products ?? []).map((p) =>
    mapProduct(p, productNames)
  );

  const userInfo = {
    id: 0,
    email: plan.email,
    first_name: profile.first_name ?? plan.first_name,
    last_name: profile.last_name ?? plan.last_name,
    full_name: profile.full_name ?? plan.full_name,
    type: profile.type ?? 'professional',
    title: profile.title,
    role: profile.role,
    profession: profile.profession,
    disciple: profile.disciple,
    contract: profile.contract,
    fiscal_code: profile.fiscal_code,
    order_of_psychologists: profile.order_of_psychologists,
    order_number: profile.order_number,
    region: profile.region,
    address: profile.address,
    website: profile.website,
    is_public: profile.is_public,
    profile_photo_path: profile.profile_photo_path,
    stripe_id: profile.stripe_id,
    vat_number: profile.vat_number,
    sdi_number: profile.sdi_number,
    legacy_user_id: plan.legacy_user_id,
    legacy_source: 'v1',
    created_at: profile.created_at ?? plan.created_at ?? null,
    updated_at: null,
  } as UserInfo;

  return {
    userInfo,
    userCourses,
    userProducts,
    remaps: plan.remaps,
    report: plan.report,
    counts: {
      courses: userCourses.length,
      courseProducts: userProducts.filter((p) => p.source === 'course').length,
      videoProducts: userProducts.filter((p) => p.source === 'video').length,
      clinicalProducts: userProducts.filter(
        (p) => p.source === 'exposure' || p.source === 'performance'
      ).length,
    },
  };
};
